import axios from "axios";
import React, { useContext, useEffect, useReducer, useState } from "react";
import Button from "react-bootstrap/Button";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LoadingBox from "../components/LoadingBox";
import MessageBox from "../components/MessageBox";
import { Store } from "../Store";
import { getError } from "../utils";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import Pagination from "react-bootstrap/Pagination";
import "./DataTable.css";

const reducer = (state, action) => {
  switch (action.type) {
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return {
        ...state,
        orders: action.payload.orders,
        page: action.payload.page,
        pages: action.payload.pages,
        count: action.payload.count,
        loading: false,
      };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "DELETE_REQUEST":
      return { ...state, loadingDelete: true, successDelete: false };
    case "DELETE_SUCCESS":
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case "DELETE_FAIL":
      return { ...state, loadingDelete: false };
    case "DELETE_RESET":
      return { ...state, loadingDelete: false, successDelete: false };
    default:
      return state;
  }
};

export default function OrderListScreen() {
  const navigate = useNavigate();
  const { state } = useContext(Store);
  const { userInfo } = state;

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [
    { loading, error, orders, pages, count, loadingDelete, successDelete },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: "",
    orders: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: "FETCH_REQUEST" });
        const { data } = await axios.get(
          `/api/orders?page=${page}&search=${query}&sortBy=${sortBy}&sortOrder=${sortOrder}`,
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        dispatch({ type: "FETCH_SUCCESS", payload: data });
      } catch (err) {
        dispatch({
          type: "FETCH_FAIL",
          payload: getError(err),
        });
      }
    };
    if (successDelete) {
      dispatch({ type: "DELETE_RESET" });
    } else {
      fetchData();
    }
  }, [userInfo, successDelete, page, query, sortBy, sortOrder]);

  const deleteHandler = async (order) => {
    if (window.confirm("Are you sure to delete?")) {
      try {
        dispatch({ type: "DELETE_REQUEST" });
        await axios.delete(`/api/orders/${order._id}`, {
          headers: { Authorization: `Bearer ${userInfo.token}` },
        });
        toast.success("Order deleted successfully");
        dispatch({ type: "DELETE_SUCCESS" });
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: "DELETE_FAIL",
        });
      }
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
    return sortOrder === "asc" ?
      <i className="fas fa-sort-up ms-1 text-primary"></i> :
      <i className="fas fa-sort-down ms-1 text-primary"></i>;
  };

  return (
    <Container className="py-4">
      <Helmet>
        <title>Order History</title>
      </Helmet>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="fw-bold mb-0">Order History</h1>
          <p className="text-muted mb-0">Manage and track all past orders ({count || 0} total)</p>
        </Col>
        <Col md={4}>
          <InputGroup className="search-bar-container shadow-sm">
            <InputGroup.Text className="bg-white border-end-0">
              <i className="fas fa-search text-muted"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search table, customer or ID..."
              className="border-start-0 ps-0 pe-3 py-2"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </InputGroup>
        </Col>
      </Row>

      {loadingDelete && <LoadingBox></LoadingBox>}
      {loading ? (
        <LoadingBox></LoadingBox>
      ) : error ? (
        <MessageBox variant="danger">{error}</MessageBox>
      ) : (
        <div className="data-table-container">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("_id")} className={sortBy === "_id" ? "active-sort" : ""}>
                    ID {getSortIcon("_id")}
                  </th>
                  <th onClick={() => handleSort("tableNumber")} className={sortBy === "tableNumber" ? "active-sort" : ""}>
                    TABLE {getSortIcon("tableNumber")}
                  </th>
                  <th onClick={() => handleSort("customerName")} className={sortBy === "customerName" ? "active-sort" : ""}>
                    CUSTOMER {getSortIcon("customerName")}
                  </th>
                  <th onClick={() => handleSort("createdAt")} className={sortBy === "createdAt" ? "active-sort" : ""}>
                    DATE {getSortIcon("createdAt")}
                  </th>
                  <th onClick={() => handleSort("totalPrice")} className={sortBy === "totalPrice" ? "active-sort" : ""}>
                    TOTAL {getSortIcon("totalPrice")}
                  </th>
                  <th>PAID</th>
                  <th>STATUS</th>
                  <th className="text-center">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td className="fw-bold text-primary">#{order._id.substring(order._id.length - 6)}</td>
                    <td>
                      {order.locationType === 'room' ? 'Room ' : 'Table '}
                      {order.tableNumber || "N/A"}
                    </td>
                    <td>{order.user ? order.user.name : (order.customerName || "Guest")}</td>
                    <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                    <td className="fw-bold">{order.totalPrice.toLocaleString()} RWF</td>
                    <td>
                      {order.isPaid ? (
                        <span className="badge bg-success-subtle text-success border border-success-subtle rounded-pill">Paid</span>
                      ) : (
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle rounded-pill">Unpaid</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge rounded-pill bg-opacity-10 
                        ${order.orderStatus === 'completed' ? 'bg-secondary text-secondary' :
                          order.orderStatus === 'cancelled' ? 'bg-danger text-danger' : 'bg-primary text-primary'}`}>
                        {(order.orderStatus || 'Pending').toUpperCase()}
                      </span>
                    </td>
                    <td className="text-center">
                      <Button
                        variant="light"
                        size="sm"
                        className="me-2 rounded-pill shadow-sm"
                        onClick={() => navigate(`/order/${order._id}`)}
                      >
                        <i className="fas fa-eye me-1"></i> Details
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="rounded-circle shadow-sm"
                        onClick={() => deleteHandler(order)}
                        style={{ width: '32px', height: '32px', padding: '0' }}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
                {orders.length === 0 && (
                  <tr>
                    <td colSpan="8" className="text-center py-5 text-muted">
                      No orders found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="pagination-container">
              <Pagination>
                <Pagination.Prev
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                />
                {[...Array(pages).keys()].map((x) => (
                  <Pagination.Item
                    key={x + 1}
                    active={x + 1 === page}
                    onClick={() => setPage(x + 1)}
                  >
                    {x + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={page === pages}
                  onClick={() => setPage(page + 1)}
                />
              </Pagination>
            </div>
          )}
        </div>
      )}
    </Container>
  );
}
